import React, { startTransition } from "react";
import {
  Avatar,
  Box,
  Chip,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SpaceDashboardIcon from "@mui/icons-material/SpaceDashboard";
import FolderIcon from "@mui/icons-material/Folder";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PeopleIcon from "@mui/icons-material/People";
import SecurityIcon from "@mui/icons-material/Security";
import HistoryIcon from "@mui/icons-material/History";
import ScienceIcon from "@mui/icons-material/Science";
import LogoutIcon from "@mui/icons-material/Logout";
import { useLocation, useNavigate } from "react-router-dom";
import useAuth from "../features/auth/hooks/useAuth";
import { useUpload } from "../features/globalUpload/GlobalUploadProvider";
import aggregateService from "../features/aggregates/aggregateService";
import { onUploadComplete } from "../lib/uploadEvents";
import { useProjects } from "./ProjectsContext";

type NavKey =
  | "overview"
  | "projects"
  | "test-builds"
  | "uploads"
  | "users"
  | "access-rules"
  | "activity";

function isNavItemActive(key: NavKey, pathname: string): boolean {
  switch (key) {
    case "overview":
      return pathname === "/dashboard";
    case "projects":
      return (
        pathname.startsWith("/projects") ||
        (pathname.startsWith("/dashboard/") && pathname !== "/dashboard/uploads")
      );
    case "test-builds":
      return pathname.startsWith("/test-builds");
    case "users":
      return pathname.startsWith("/users");
    case "access-rules":
      return pathname.startsWith("/access-rules");
    case "activity":
      return pathname.startsWith("/activity");
    default:
      return false;
  }
}

interface SidebarProps {
  width: number;
  permanent: boolean;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

const ROLE_LABELS: Record<string, string> = {
  superuser: "Superuser",
  user: "User",
};

const Sidebar: React.FC<SidebarProps> = ({
  width,
  permanent,
  mobileOpen,
  onMobileClose,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { profile, logout } = useAuth();
  const { openUpload } = useUpload();
  const { projects, loading, error } = useProjects();

  // Test builds expiring within 24 h, from the overview summary. A failed
  // request (for example a missing index for non-admin accounts) shows no count.
  const [expiringCount, setExpiringCount] = React.useState<number | null>(null);
  React.useEffect(() => {
    const load = () =>
      aggregateService
        .getOverviewSummary()
        .then((s) => setExpiringCount(s.testBuildsExpiring24h))
        .catch(() => setExpiringCount(null));
    load();
    return onUploadComplete(({ type }) => {
      if (type === "testBuild") load();
    });
  }, []);

  const go = (to: string) => {
    startTransition(() => navigate(to));
    if (!permanent) onMobileClose();
  };

  const itemSx = {
    borderRadius: 1,
    mb: 0.25,
    minHeight: 44,
    color: "inherit",
    "&.Mui-selected": {
      bgcolor: "primary.light",
      color: "text.primary",
      "&:hover": { bgcolor: "primary.light" },
      "& .MuiListItemIcon-root": { color: "text.primary" },
    },
    "&:hover": { bgcolor: (t: any) => alpha(t.palette.common.white, 0.1) },
  };

  const iconSx = { minWidth: 36, color: "inherit" };

  const groupHeaderSx = {
    bgcolor: "transparent",
    color: "inherit",
    opacity: 0.75,
    lineHeight: "28px",
    fontSize: 11,
    letterSpacing: 1,
    textTransform: "uppercase" as const,
    px: 1.5,
  };

  const name = profile?.name || profile?.email?.split("@")[0] || "Admin";
  const initial = (profile?.name || profile?.email || "U")[0].toUpperCase();
  const roleLabel = ROLE_LABELS[profile?.role ?? ""] ?? "Member";

  const renderItem = (
    key: NavKey,
    label: string,
    icon: React.ReactNode,
    onClick: () => void,
    badge?: React.ReactNode
  ) => (
    <ListItemButton
      key={key}
      selected={isNavItemActive(key, pathname)}
      onClick={onClick}
      sx={itemSx}
    >
      <ListItemIcon sx={iconSx}>{icon}</ListItemIcon>
      <ListItemText
        primary={label}
        slotProps={{ primary: { sx: { fontSize: 14, fontWeight: 600, color: "inherit" } } }}
      />
      {badge}
    </ListItemButton>
  );

  return (
    <Drawer
      variant={permanent ? "permanent" : "temporary"}
      {...(permanent ? {} : { open: mobileOpen, onClose: onMobileClose })}
      sx={{ width: permanent ? width : 0, flexShrink: 0 }}
      slotProps={{
        paper: {
          sx: {
            width,
            bgcolor: "sidebar.background",
            color: "sidebar.text",
            borderRight: 0,
            display: "flex",
            flexDirection: "column",
            p: 1.5,
          },
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 0.5, py: 1, mb: 1 }}>
        <Box
          component="img"
          src={require("../assets/growsari.png")}
          alt="Growsari"
          sx={{ height: 44, width: "auto" }}
        />
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: "sidebar.textActive" }}
        >
          GS Upload Portal
        </Typography>
      </Box>

      <List
        dense
        subheader={<ListSubheader sx={groupHeaderSx}>Workspace</ListSubheader>}
      >
        {renderItem(
          "overview",
          "Overview",
          <SpaceDashboardIcon fontSize="small" />,
          () => go("/dashboard")
        )}
        {renderItem(
          "projects",
          "Projects",
          <FolderIcon fontSize="small" />,
          () => go("/projects"),
          !loading && !error ? (
            <Chip
              size="small"
              label={projects.length}
              sx={{
                height: 18,
                fontSize: 11,
                color: "inherit",
                bgcolor: (t) => alpha(t.palette.common.white, 0.18),
              }}
            />
          ) : undefined
        )}
        {renderItem(
          "test-builds",
          "Test builds",
          <ScienceIcon fontSize="small" />,
          () => go("/test-builds"),
          expiringCount !== null && expiringCount > 0 ? (
            <Tooltip title="Expiring within 24 h">
              <Chip size="small" color="warning" label={expiringCount} sx={{ height: 18, fontSize: 11, fontWeight: 700 }} />
            </Tooltip>
          ) : undefined
        )}
        {renderItem(
          "uploads",
          "Uploads",
          <CloudUploadIcon fontSize="small" />,
          () => {
            openUpload();
            if (!permanent) onMobileClose();
          }
        )}
      </List>

      {profile?.role === "superuser" && (
        <List
          dense
          subheader={<ListSubheader sx={groupHeaderSx}>Admin</ListSubheader>}
        >
          {renderItem(
            "users",
            "Users",
            <PeopleIcon fontSize="small" />,
            () => go("/users")
          )}
          {renderItem(
            "access-rules",
            "Access rules",
            <SecurityIcon fontSize="small" />,
            () => go("/access-rules")
          )}
          {renderItem(
            "activity",
            "Activity",
            <HistoryIcon fontSize="small" />,
            () => go("/activity")
          )}
        </List>
      )}

      <Box
        sx={{
          mt: "auto",
          pt: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderTop: "1px solid",
          borderColor: (t) => alpha(t.palette.common.white, 0.1),
        }}
      >
        <Avatar
          sx={{ width: 28, height: 28, fontSize: "0.75rem", bgcolor: "primary.light", color: "text.primary" }}
        >
          {initial}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="caption"
            noWrap
            sx={{ display: "block", fontWeight: 600, color: "sidebar.textActive" }}
          >
            {name}
          </Typography>
          <Typography
            variant="caption"
            noWrap
            title={profile?.email}
            sx={{ display: "block", fontSize: 11, color: "inherit" }}
          >
            {profile?.email}
          </Typography>
          <Typography variant="caption" sx={{ display: "block", fontSize: 11, color: "inherit", opacity: 0.7 }}>
            {roleLabel}
          </Typography>
        </Box>
        <Tooltip title="Logout">
          <IconButton
            size="small"
            sx={{ color: "inherit" }}
            onClick={() =>
              startTransition(() => {
                logout();
              })
            }
          >
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
